/**
 * TokensContext + useTokens — make the singleton `WebTokenStorage` reachable
 * from the React tree without threading it through every component prop.
 *
 * `main.tsx` instantiates the token storage once and wraps the tree with
 * <TokensProvider>. The AuthGuard already accepts `tokens` as a prop (kept
 * that way so the T7 tests don't need a provider); new T8 surfaces pull it
 * via the hook to avoid prop-drilling.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { WebTokenStorage } from './tokenStorage';

const TokensContext = createContext<WebTokenStorage | null>(null);

export interface TokensProviderProps {
  tokens: WebTokenStorage;
  children: ReactNode;
}

export function TokensProvider({
  tokens,
  children,
}: TokensProviderProps): JSX.Element {
  return (
    <TokensContext.Provider value={tokens}>{children}</TokensContext.Provider>
  );
}

export function useTokens(): WebTokenStorage {
  const tokens = useContext(TokensContext);
  if (!tokens) {
    throw new Error(
      'useTokens must be used inside <TokensProvider>. ' +
        'main.tsx wraps the tree; tests should mount one with makeTokenStorage().'
    );
  }
  return tokens;
}
